import Button from '@rio-cloud/rio-uikit/Button';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import MarkdownRenderers from '@/components/molecule/MarkdownRenderers/MarkdownRenderers';
import { THandleSendParams } from '@/components/pages/ChatPage/types';
import { getLocale } from '@/configuration/lang/langSlice';
import { useAppDispatch, useAppSelector } from '@/configuration/setup/hooks';
import { useOnboardingMessages } from '@/hooks/useOnboardingMessages';
import { selectMessageById } from '@/store/chat/chat.slice';
import {
  disableDemoFlow,
  finishDemoFlow,
  resetStep,
  selectIsDemoFlowEnabled,
  selectIsOnboardingMode,
  selectIsOnboardingQuestionLoading,
  selectOnboardingMessageId,
  selectOnboardingStep,
  selectShowOnboardingTip,
  showOnboardingTip,
  steps,
} from '@/store/onboarding/onboardingSlice';
import { ONBOARDING_THREAD_ID } from '@/utils/constants';

import OnboardingDraggableTip from '../../organism/OnboardingDraggableTip/OnboardingDraggableTip';
import { MarkdownWrapper, TextMessage } from './Style';

type BubbleContentProps = {
  messageId: string;
  isLoading: boolean;
  isEditing: boolean;
  setIsEditing: (value: boolean) => void;
  handleSend: (params: THandleSendParams) => void;
};

const texts = {
  titles: {
    wellDone: 'chatPage.onboarding.wellDone',
    onboardingComplete: 'chatPage.onboarding.onboardingCompleteTitle',
  },
  contents: {
    tryNextQuestion: 'chatPage.onboarding.tryNextQuestion',
    onboardingComplete: 'chatPage.onboarding.onboardingCompleteContent',
  },
  buttons: {
    skip: 'chatPage.onboarding.buttonSkip',
    tryNextQuestion: 'chatPage.onboarding.buttonTryNextQuestion',
    okThanks: 'chatPage.onboarding.buttonOkThanks',
  },
  icons: {
    skipIcon: 'icon-eye-close',
    sendIcon: 'icon-send',
    repeat: 'icon-repeat',
  },
  sendButton: 'chatPage.SpeechBubble.edit.button.send',
  cancelButton: 'chatPage.SpeechBubble.edit.button.cancel',
  roleLabels: {
    ai: 'chatPage.SpeechBubble.role.ai.label',
    you: 'chatPage.SpeechBubble.role.you.label',
  },
};

const BubbleContent = ({ messageId, isEditing, setIsEditing, isLoading, handleSend }: BubbleContentProps) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const { content, isOnboardingQuestion, data_visualization, threadId } = useAppSelector(selectMessageById(messageId));
  const isOnboardingQuestionLoading = useAppSelector(selectIsOnboardingQuestionLoading);
  const onboardingStep = useAppSelector(selectOnboardingStep);
  const isOnboardingMode = useAppSelector(selectIsOnboardingMode);
  const isOnboardingTipShow = useAppSelector(selectShowOnboardingTip);
  const isOnboardingDemoEnabled = useAppSelector(selectIsDemoFlowEnabled);
  const onboardingMessageId = useAppSelector(selectOnboardingMessageId);
  const currentLanguage = useAppSelector(getLocale);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [message, setMessage] = useState(content);
  const regex = /\bragDocuments\.[^\s]+/g;

  const questions = useOnboardingMessages(currentLanguage);

  const getNextQuestion = (stepIndex: number) => {
    const nextStep = steps[stepIndex];
    const stepMessages = questions[nextStep]?.messages?.content;

    const humanQuestions = stepMessages?.filter((message) => message.role === 'human');
    return humanQuestions?.length > 0 ? humanQuestions[humanQuestions.length - 1].content : '';
  };

  const handleNextStep = () => {
    const currentStepIndex = onboardingStep !== 'step-0' ? steps.indexOf(onboardingStep) : -1;
    const nextStepIndex = currentStepIndex + 1 < steps.length ? currentStepIndex + 1 : 0;
    const nextStep = steps[nextStepIndex];

    dispatch({ type: 'onboarding/setStep', payload: nextStep });

    const nextQuestion = getNextQuestion(nextStepIndex);

    if (onboardingStep === 'step-4') {
      dispatch(resetStep());
    } else {
      handleSend({
        content: nextQuestion,
        isOnboarding: true,
        nextStepIndex: nextStep,
      });
    }
  };

  const handleOnHide = () => {
    if (onboardingStep === 'step-4') {
      dispatch(finishDemoFlow());
      dispatch(disableDemoFlow());
    }
  };

  const handleEditSubmit = () => {
    handleSend({ content: message, isOnboarding: false });
    setIsEditing(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleEditSubmit();
    }
  };

  const handleEditCancel = () => {
    setIsEditing(false);
  };

  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  };

  useEffect(() => {
    if (isOnboardingQuestion && messageId) {
      dispatch(showOnboardingTip({ messageId }));
    }
  }, [isOnboardingQuestion, messageId, dispatch]);

  useEffect(() => {
    if (isEditing) {
      adjustTextareaHeight();
    }
  }, [isEditing, message]);

  const isMultiline = (text: string) => text.includes('\n');

  const fixDashSpacing = (text: string): string => {
    return text
      .split('\n')
      .map((line) => (line.startsWith('-') && !line.startsWith('- ') ? '- ' + line.slice(1).trimStart() : line))
      .join('\n');
  };

  const finalContent = useMemo(() => {
    const fixedContent = fixDashSpacing(content);
    const lines = fixedContent.split('\n');

    const newLines: string[] = [];

    lines.forEach((line, index) => {
      newLines.push(line);
      const trimmedLine = line.trim();
      const nextLine = lines[index + 1];

      if ((trimmedLine.startsWith('-') && nextLine && nextLine.trim() !== '') || isMultiline(fixedContent)) {
        if (newLines[newLines.length - 1] !== '') {
          newLines.push('');
        }
      }
    });

    return newLines.join('\n');
  }, [content]);

  return (
    <TextMessage>
      {isEditing ? (
        <div className="form-group width-100pct display-flex flex-column">
          <textarea
            ref={textareaRef}
            className="form-control"
            id="textArea"
            value={message}
            onKeyDown={handleKeyDown}
            onChange={(e) => setMessage(e.target.value)}
            rows={5}
            cols={100}
          />
          <div className="display-flex gap-10 justify-content-end margin-top-10">
            <Button className="btn btn-outline btn-secondary" onClick={handleEditCancel}>
              {intl.formatMessage({ id: texts.cancelButton })}
            </Button>
            <Button
              data-testid="send-edited-message"
              className="btn btn-primary"
              onClick={handleEditSubmit}
              disabled={isLoading}
            >
              {intl.formatMessage({ id: texts.sendButton })}
            </Button>
          </div>
        </div>
      ) : null}
      <OnboardingDraggableTip
        show={
          !isLoading &&
          (isOnboardingQuestion ?? false) &&
          isOnboardingMode &&
          isOnboardingTipShow &&
          isOnboardingDemoEnabled &&
          messageId === onboardingMessageId &&
          !isOnboardingQuestionLoading &&
          threadId === ONBOARDING_THREAD_ID
        }
        title={
          onboardingStep !== 'step-4'
            ? intl.formatMessage({ id: texts.titles.wellDone })
            : intl.formatMessage({ id: texts.titles.onboardingComplete })
        }
        content={
          onboardingStep !== 'step-4'
            ? intl.formatMessage({ id: texts.contents.tryNextQuestion })
            : intl.formatMessage({ id: texts.contents.onboardingComplete })
        }
        nextButton={{
          iconName: onboardingStep !== 'step-4' ? texts.icons.sendIcon : texts.icons.repeat,
          text:
            onboardingStep !== 'step-4'
              ? intl.formatMessage({ id: texts.buttons.tryNextQuestion })
              : intl.formatMessage({ id: texts.buttons.okThanks }),
          onClick: onboardingStep !== 'step-4' ? handleNextStep : handleOnHide,
        }}
      />
      <MarkdownWrapper>
        <ReactMarkdown
          components={MarkdownRenderers({
            message: finalContent,
            showActions: true,
            data_visualization: data_visualization,
            messageId: messageId,
            opensSourcesInViewer: true,
          })}
          remarkPlugins={[remarkGfm]}
        >
          {!isEditing
            ? finalContent.replace(regex, (match) => intl.formatMessage({ id: `chatPage.${match}` }))
            : undefined}
        </ReactMarkdown>
      </MarkdownWrapper>
    </TextMessage>
  );
};

export default BubbleContent;
